// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import { applySentryBeforeSend, scrubSentrySensitiveData } from '@/code/lib/sentry/applySentryBeforeSend';
import { isBrowserTrpcClientError } from '@/code/lib/sentry/trpcErrorPolicy.browser';

// Safely check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Prepare integrations based on environment
const integrations = [];

// Only add browser-specific integrations when in a browser environment
if (isBrowser) {
  // Add replay integration
  if (Sentry.replayIntegration) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      })
    );
  }

  // Add browser profiling integration
  if (Sentry.browserProfilingIntegration) {
    integrations.push(Sentry.browserProfilingIntegration());
  }

  // Add browser tracing integration
  if (Sentry.browserTracingIntegration) {
    integrations.push(
      Sentry.browserTracingIntegration({
        // Automatically instrument React components
        idleTimeout: 5000,
        traceFetch: true,
        traceXHR: true,
      })
    );
  }
}

integrations.push(Sentry.consoleLoggingIntegration())
Sentry.init({
  dsn: 'https://cd8584af67492d5fde4d41b465b4915d@o4508124573663232.ingest.us.sentry.io/4508124577726464',

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 1,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,
  enabled: process.env.NODE_ENV !== 'development',
  maxValueLength: 8192,
  sendDefaultPii: false,
  integrations,
  beforeSendTransaction: scrubSentrySensitiveData,
  beforeSendLog: scrubSentrySensitiveData,
  beforeSend(event, hint) {
    return applySentryBeforeSend(event, hint, isBrowserTrpcClientError);
  },

  // Only add these browser-specific options when in browser environment
  ...(isBrowser && {
    profilesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  }),
});

/**
 * Set user information for Sentry error tracking
 * Call this function when user data is available (after login/auth)
 * 
 * Example usage:
 * import { setUserForSentry } from '../path/to/this/file';
 * 
 * // After user authentication or when user data is loaded
 * setUserForSentry({
 *   id: 'user-123',
 *   email: 'user@example.com',
 *   username: 'username'
 * });
 */
export function setUserForSentry(user: {
  id?: string;
  email?: string;
  username?: string;
  [key: string]: unknown;
}) {
  Sentry.setUser({ id: user.id });
}

// To clear user data (e.g., on logout)
export function clearUserForSentry() {
  Sentry.setUser(null);
}
